import {
    useContext,
    useState,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    View
} from "react-native";
import type IChartSkeletonProps from "./type";
import stylesheet, {
    CHART_SKELETON_BAR_HEIGHTS,
    CHART_SKELETON_GRID_LINES,
    getChartSkeletonHoleSize,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    ChartContext
} from "../../context";
import Skeleton from "../../../skeleton";

const ChartSkeleton: FC<IChartSkeletonProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    plotSize = "medium",
    height: heightProp,
    onLayout,
    style,
    kind,
    ...props
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        shapeConfigs,
        surfaceColor,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalizeProp ?? context?.customLocalize);

    const height = heightProp ?? shapeConfigs.chartHeight[plotSize];

    const [
        width,
        setWidth
    ] = useState<number>(0);

    const {
        container: containerDynamicStyle,
        grid: gridDynamicStyle,
        hole: holeDynamicStyle,
        plot: plotDynamicStyle,
        bar: barDynamicStyle
    } = useStyles({
        shapeConfigs,
        surfaceColor,
        radiuses,
        borders,
        colors,
        spaces,
        height,
        kind
    });

    const handleLayout = (event: LayoutChangeEvent) => {
        setWidth(event.nativeEvent.layout.width);

        onLayout?.(event);
    };

    const renderBars = () => {
        const barArea = height - spaces.spacingLg;

        return <View
            style={[
                stylesheet.bars,
                plotDynamicStyle
            ]}
        >
            {
                CHART_SKELETON_BAR_HEIGHTS.map((ratio, index) => <Skeleton
                    radius={radiuses.chartBar}
                    customTheme={customTheme}
                    height={barArea * ratio}
                    style={[
                        stylesheet.bar,
                        barDynamicStyle
                    ]}
                    width="auto"
                    key={index}
                />)
            }
        </View>;
    };

    const renderLine = () => {
        const plotHeight = height - spaces.spacingLg - borders.line;

        return <View
            style={plotDynamicStyle}
        >
            <View
                style={stylesheet.area}
            >
                <Skeleton
                    customTheme={customTheme}
                    radius={radiuses.xs}
                    height={plotHeight}
                />
                {
                    CHART_SKELETON_GRID_LINES.map((ratio) => <View
                        style={[
                            stylesheet.grid,
                            gridDynamicStyle,
                            {
                                top: plotHeight * ratio
                            }
                        ]}
                        key={ratio}
                    />)
                }
            </View>
        </View>;
    };

    const renderRound = () => {
        const available = height - spaces.spacingSm * 2;

        const size = Math.max(0, width ? Math.min(available, width) : available);

        const holeSize = getChartSkeletonHoleSize({
            shapeConfigs,
            kind,
            size
        });

        return <View
            style={[
                stylesheet.round,
                plotDynamicStyle
            ]}
        >
            <Skeleton
                customTheme={customTheme}
                variant="circle"
                size={size}
            />
            {
                holeSize > 0 ?
                    <View
                        style={[
                            stylesheet.hole,
                            holeDynamicStyle,
                            {
                                height: holeSize,
                                width: holeSize
                            }
                        ]}
                    />
                :
                    null
            }
        </View>;
    };

    const renderShape = () => {
        if(kind === "bars") {
            return renderBars();
        }

        if(kind === "line") {
            return renderLine();
        }

        return renderRound();
    };

    return <View
        {...props}
        accessibilityLabel={localize("chart-loading")}
        aria-label={localize("chart-loading")}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        onLayout={handleLayout}
        aria-busy={true}
        role="status"
    >
        {renderShape()}
    </View>;
};
export default ChartSkeleton;
